/**
 * BillingPage - Subscription management page
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useAuthStore, useSubscriptionStore } from '@/stores';
import { SUBSCRIPTION_TIERS } from '../config';
import { UsageMeter } from '../components/UsageMeter';
import { SubscriptionBadge } from '../components/SubscriptionBadge';

export function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const {
    tier,
    subscription,
    fetchSubscription,
    cancelSubscription,
    getLocalizedPrice,
    isLoading,
  } = useSubscriptionStore();
  const [verifying, setVerifying] = useState(false);

  const success = searchParams.get('success') === 'true';
  const sessionId = searchParams.get('session_id');
  const tierConfig = SUBSCRIPTION_TIERS[tier];
  const price = getLocalizedPrice(tierConfig.priceUSD);

  // After Stripe checkout, verify and redirect FAST
  useEffect(() => {
    if (success && sessionId && !verifying) {
      setVerifying(true);
      fetch(`/api/verify-checkout?session_id=${sessionId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            // Set tier directly in store — skip slow re-fetch
            useSubscriptionStore.setState({
              tier: 'pro',
              subscription: { status: 'trialing', tier: 'pro' } as any,
            });
            // Redirect immediately
            navigate('/plan-route', { replace: true });
            // Refresh full data in background (non-blocking)
            fetchSubscription();
          } else {
            setVerifying(false);
            fetchSubscription();
          }
        })
        .catch(() => {
          setVerifying(false);
          fetchSubscription();
        });
    }
  }, [success, sessionId]);

  useEffect(() => {
    // Don't fetch on success redirect — verify-checkout already set the tier correctly.
    // Fetching here races with verify-checkout and can overwrite 'pro' back to 'free'.
    if (isAuthenticated && !verifying && !success) {
      fetchSubscription();
    }
  }, [isAuthenticated, fetchSubscription, success, verifying]);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  // Show loading while verifying checkout
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-hud-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg font-semibold">Activating your Pro trial...</p>
          <p className="text-text-secondary text-sm mt-2">Setting up your subscription</p>
        </div>
      </div>
    );
  }

  const handleCancelSubscription = async () => {
    if (confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      await cancelSubscription();
    }
  };

  const handleManageBilling = () => {
    // Redirect to Stripe Customer Portal
    window.location.href = '/api/customer-portal';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-hud-primary/5 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between p-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-text-secondary hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to App
        </button>
      </nav>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-8">
        {/* Success message */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-xl bg-green-500/10 border border-green-500/30"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-green-400">Welcome to {tierConfig.name}!</p>
                <p className="text-sm text-green-400/80">Your subscription is now active.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Subscription</h1>
          <p className="text-text-secondary">Manage your subscription and view usage</p>
        </div>

        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'p-6 rounded-2xl mb-6',
            'bg-glass-bg backdrop-blur-xl border border-glass-border'
          )}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm text-text-secondary mb-1">Current Plan</p>
              <h2 className="text-2xl font-bold text-white">{tierConfig.name}</h2>
            </div>
            <SubscriptionBadge showUpgrade={false} />
          </div>

          {tier !== 'free' && (
            <>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-white">{price.formatted}</span>
                <span className="text-text-secondary">/month</span>
              </div>

              {subscription?.current_period_end && (
                <p className="text-sm text-text-secondary">
                  {subscription.cancel_at_period_end ? (
                    <span className="text-yellow-400">
                      Cancels on {new Date(subscription.current_period_end).toLocaleDateString()}
                    </span>
                  ) : (
                    <>Next billing date: {new Date(subscription.current_period_end).toLocaleDateString()}</>
                  )}
                </p>
              )}
            </>
          )}

          <div className="flex gap-3 mt-6">
            {tier === 'free' ? (
              <button
                onClick={() => navigate('/pricing')}
                className={cn(
                  'flex-1 py-3 rounded-xl font-semibold text-center',
                  'bg-gradient-to-r from-hud-primary to-teal-600',
                  'text-white',
                  'hover:shadow-lg hover:shadow-hud-primary/25',
                  'transition-all duration-200'
                )}
              >
                Upgrade Plan
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/pricing')}
                  className={cn(
                    'flex-1 py-3 rounded-xl font-semibold text-center',
                    'bg-glass-bg border border-glass-border',
                    'text-text-primary',
                    'hover:bg-slate-700',
                    'transition-all duration-200'
                  )}
                >
                  Change Plan
                </button>
                <button
                  onClick={handleManageBilling}
                  className={cn(
                    'flex-1 py-3 rounded-xl font-semibold text-center',
                    'bg-glass-bg border border-glass-border',
                    'text-text-primary',
                    'hover:bg-slate-700',
                    'transition-all duration-200'
                  )}
                >
                  Manage Billing
                </button>
              </>
            )}
          </div>
        </motion.div>

        {/* Usage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <UsageMeter />
        </motion.div>

        {/* Cancel subscription */}
        {tier !== 'free' && !subscription?.cancel_at_period_end && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 rounded-2xl bg-glass-bg backdrop-blur-xl border border-glass-border"
          >
            <h3 className="font-semibold text-white mb-2">Cancel Subscription</h3>
            <p className="text-sm text-text-secondary mb-4">
              You can cancel your subscription at any time. You'll continue to have access
              until the end of your current billing period.
            </p>
            <button
              onClick={handleCancelSubscription}
              disabled={isLoading}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                'text-red-400 border border-red-400/30',
                'hover:bg-red-400/10',
                'transition-colors',
                'disabled:opacity-50'
              )}
            >
              {isLoading ? 'Processing...' : 'Cancel Subscription'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
